import React, { useEffect } from 'react';
import { ICONS } from '@components/icons/icon.tsx';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppDialog from '@components/app-dialog/app-dialog';
import AddFolderContent from '@components/media-library-component/dialog-contents/add-folder-content';
import AddMediaContent from '@components/media-library-component/dialog-contents/add-media-content';
import MoveTreeContent from '@components/media-library-component/dialog-contents/move-tree-content';
import DeleteDialog from '@components/delete-dialog/delete-dialog';
import Loader from '@components/loader/loader';
import useMediaQuery from '@mui/material/useMediaQuery';
import useMediaLibraryStore from '@store/media-library-store/media-library.store';
import BreadcrumbNavigation from '@components/media-library-component/components/media-library-content/components/breadcrumb-navigation/breadcrumb-navigation.tsx';
import MediaLibraryControls from '@components/media-library-component/components/media-library-content/components/media-library-controls/media-library-controls.tsx';
import Folders from '@components/media-library-component/components/media-library-content/components/folders/folders.tsx';
import MediaContent from '@components/media-library-component/components/media-library-content/components/media-content/media-content.tsx';
import EmptyContent from '@components/media-library-component/components/media-library-content/components/empty-content/empty-content.tsx';
import { FileTypeEnum, MediaModel } from '@models/media-library.model.ts';
import RenameMediaContent from '@components/media-library-component/dialog-contents/rename-media-content.tsx';

const MediaLibraryContent: React.FC<{
  allowedTypes?: (
    | FileTypeEnum.All
    | FileTypeEnum.Image
    | FileTypeEnum.Audio
    | FileTypeEnum.Video
    | FileTypeEnum.Document
  )[];
  mediaKey: string;
  isDialogOpen?: boolean;
}> = ({ allowedTypes, mediaKey, isDialogOpen }) => {
  const { t } = useTranslation();
  const {
    createFolder,
    moveFolder,
    renameFolder,
    deleteFolder,
    deleteMedia,
    moveMedia,
    setEditingFolder,
    setEditingMedia,
    getMediaLibraryFolders,
    getMedia,
    renameMedia,
    resetEditingFolder,
    deleteSingleMedia,
    resetEditingMedia,
    resetToggleItemChecked,
    updateFilters,
    editingFolder,
    editingMedia,
    checkedItems,
    checkedMediaItems,
    mediaData,
    foldersData,
    currentFolder,
    pagination,
    sort,
    filter,
    search,
    isLoading,
    isLoaded,
    isFolderLoading,
    isMediaLoading,
    isMediaLoaded
  } = useMediaLibraryStore();

  const [openFolderDialog, setOpenFolderDialog] = React.useState(false);
  const [openMediaDialog, setOpenMediaDialog] = React.useState(false);
  const [openRenameMediaDialog, setOpenRenameMediaDialog] =
    React.useState(false);
  const [openMoveDialog, setOpenMoveDialog] = React.useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const isTabletScreen = useMediaQuery('(max-width: 1715px)');
  const [initialized, setInitialized] = React.useState(false);

  const handleFolderOpen = () => setOpenFolderDialog(true);
  const handleFolderClose = () => {
    setOpenFolderDialog(false);
    resetEditingFolder();
  };
  const handleMediaOpen = () => setOpenMediaDialog(true);
  const handleMediaClose = () => setOpenMediaDialog(false);
  const handleMoveClose = () => setOpenMoveDialog(false);
  const handleDeleteClose = () => {
    setShowDeleteDialog(false);
    resetToggleItemChecked();
    resetEditingMedia();
  };
  const handleRenameMediaClose = () => {
    setOpenRenameMediaDialog(false);
    resetEditingMedia();
  };
  const handleEditClick = (folder: any) => {
    setEditingFolder(folder);
    setOpenFolderDialog(true);
  };

  const handleEditMediaClick = (media: MediaModel) => {
    setEditingMedia(media);
    setOpenRenameMediaDialog(true);
  };

  const handleDeleteMediaClick = (media: MediaModel) => {
    setEditingMedia(media);
    setShowDeleteDialog(true);
  };

  const handleMoveClick = () => setOpenMoveDialog(true);
  const handleDelete = () => setShowDeleteDialog(true);

  const handleDeleteConfirm = () => {
    if (editingMedia) {
      deleteSingleMedia({
        data: { path: editingMedia.path },
        handleClose: handleDeleteClose
      });
      return;
    }

    const selectedFolderIds = Object.keys(checkedItems).filter(
      (id) => checkedItems[id]
    );
    const selectedMediaIds = Object.keys(checkedMediaItems).filter(
      (id) => checkedMediaItems[id]
    );

    if (selectedFolderIds.length > 0) {
      deleteFolder({ ids: selectedFolderIds, handleClose: handleDeleteClose });
    } else if (selectedMediaIds.length > 0) {
      deleteMedia({ ids: selectedMediaIds, handleClose: handleDeleteClose });
    }
  };

  const handleFormSave = (data: { name: string }) => {
    if (editingFolder?.id) {
      renameFolder({
        data: { ...data, id: editingFolder.id },
        id: editingFolder.id,
        handleClose: handleFolderClose
      });
    } else {
      createFolder({
        data: { ...data, parentId: currentFolder!.id },
        handleClose: handleFolderClose
      });
    }
  };

  const handleRenameMedia = (data: { name: string }) => {
    if (editingMedia?._id) {
      renameMedia({
        data: { ...data },
        id: editingMedia._id,
        handleClose: handleRenameMediaClose
      });
    }
  };

  const handleMove = (targetId: string) => {
    const selectedFolderIds = Object.keys(checkedItems).filter(
      (id) => checkedItems[id]
    );
    const selectedMediaIds = Object.keys(checkedMediaItems).filter(
      (id) => checkedMediaItems[id]
    );

    if (selectedFolderIds.length > 0) {
      moveFolder({
        data: { ids: selectedFolderIds, parentId: targetId },
        handleClose: handleMoveClose
      });
    } else if (selectedMediaIds.length > 0) {
      moveMedia({
        data: { ids: selectedMediaIds, folderId: targetId },
        handleClose: handleMoveClose
      });
    }
  };

  useEffect(() => {
    if (!isDialogOpen) return;

    const hasSearchValue = Object.values(search || {}).some(
      (value) => value !== '' && value !== null && value !== undefined
    );
    const folderId = hasSearchValue ? null : currentFolder?.id || 'root';

    getMediaLibraryFolders(folderId, currentFolder?.title);
  }, [currentFolder?.id, sort, search]);

  useEffect(() => {
    if (allowedTypes && allowedTypes.length === 1 && !initialized) {
      const type = allowedTypes[0];

      if (type !== FileTypeEnum.All) {
        updateFilters('type', [type.toLowerCase()]);
      }

      setInitialized(true);
    } else if (!initialized) {
      setInitialized(true);
    }
  }, [allowedTypes, initialized, updateFilters]);

  useEffect(() => {
    if (!isDialogOpen || !currentFolder || !initialized) return;

    const hasSearchValue = Object.values(search || {}).some(
      (value) => value !== '' && value !== null && value !== undefined
    );
    const folderId = hasSearchValue ? null : currentFolder.id;

    getMedia(folderId);
  }, [
    initialized,
    currentFolder?.id,
    pagination.page,
    pagination.rowsPerPage,
    sort,
    filter,
    search
  ]);

  return (
    <div className="flex flex-col h-[79vh] max-h-[79vh] max-lg-xl:h-[78vh]">
      <div className="flex justify-between items-center gap-8">
        <div className="flex overflow-x-auto">
          <BreadcrumbNavigation />
        </div>
        <div className="flex items-center gap-3.5">
          <Button
            variant="outlined"
            startIcon={ICONS.plusBlackIcon}
            onClick={handleFolderOpen}
          >
            {t('add_folder')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={ICONS.plusIcon}
            onClick={handleMediaOpen}
          >
            {t('add_media')}
          </Button>
        </div>
      </div>

      <div className="mt-5">
        <MediaLibraryControls
          onDeleteClick={handleDelete}
          onMoveClick={handleMoveClick}
          allowedTypes={allowedTypes}
        />
      </div>

      <div className="flex-1 flex flex-col min-h-0 mt-6 max-lg-xl:mt-4">
        {(isLoading() || isMediaLoading()) && (
          <div className="flex justify-center items-center flex-1">
            <Loader />
          </div>
        )}

        {isLoaded() && isMediaLoaded() && foldersData.length > 0 && (
          <div className="mb-5">
            <Folders onEditClick={handleEditClick} />
          </div>
        )}

        {isMediaLoaded() && isLoaded() && (
          <div className="flex-1 min-h-0 overflow-hidden">
            {mediaData && Object.keys(mediaData).length > 0 ? (
              <MediaContent
                mediaKey={mediaKey}
                onEditClick={handleEditMediaClick}
                onDeleteClick={handleDeleteMediaClick}
              />
            ) : (
              <EmptyContent
                onFolderOpen={handleFolderOpen}
                onMediaOpen={handleMediaOpen}
              />
            )}
          </div>
        )}
      </div>

      <AppDialog
        open={openFolderDialog}
        size="sm"
        title={t('add_folder')}
        content={
          <AddFolderContent
            updateFolderData={editingFolder}
            onSave={handleFormSave}
          />
        }
        onClose={handleFolderClose}
      />
      <AppDialog
        open={openMediaDialog}
        size={isTabletScreen ? 'lg' : 'xl'}
        title={t('add_media')}
        content={<AddMediaContent handleClose={handleMediaClose} />}
        onClose={handleMediaClose}
      />

      <AppDialog
        open={openRenameMediaDialog}
        size="sm"
        title={t('rename_media')}
        content={
          <RenameMediaContent
            updateMediaData={editingMedia}
            onSave={handleRenameMedia}
          />
        }
        onClose={handleRenameMediaClose}
      />

      <AppDialog
        open={openMoveDialog}
        size={isTabletScreen ? 'sm' : 'md'}
        title={t('move_elements_to')}
        content={<MoveTreeContent onMove={handleMove} />}
        onClose={handleMoveClose}
      />

      <DeleteDialog
        onCancel={handleDeleteClose}
        onDelete={handleDeleteConfirm}
        open={showDeleteDialog}
        isLoading={isFolderLoading() || isMediaLoading()}
        title={
          Object.values(checkedItems).filter(Boolean).length > 1 ||
          Object.values(checkedMediaItems).filter(Boolean).length > 1
            ? t('delete_popup_title_many_items', {
                count:
                  Object.values(checkedItems).filter(Boolean).length +
                  Object.values(checkedMediaItems).filter(Boolean).length
              })
            : t('delete_popup_title')
        }
      />
    </div>
  );
};

export default MediaLibraryContent;
